import { isCheckoutInFlight } from 'utils/posCheckoutLock'

/** Session-only guard — prevent tight reload loops if the origin is genuinely broken */
const SESSION_RELOAD_ATTEMPTS_KEY = 'erp_chunk_recovery_reload_attempts'
const SESSION_AUTO_RELOAD_ATTEMPTS_KEY = 'erp_chunk_recovery_auto_reload_attempts'
const SESSION_MAX_FULL_RECOVERY_RELOADS = 3
const SESSION_MAX_AUTO_RECOVERY_RELOADS = 1
const GLOBAL_PANEL_ID = 'erp-global-chunk-recovery-panel'
const CACHE_BUST_PARAM = '__erp_chunk_reload'
const DEPLOYMENT_SIGNATURE_KEY = 'erp_deployment_signature'
const DEPLOYMENT_CHECK_INTERVAL_MS = 2 * 60 * 1000

export function isChunkLoadError(error: unknown): boolean {
 if (!error || typeof error !== 'object') return false
 const e = error as { name?: string; message?: string }
 const msg = e.message ?? ''
 const name = e.name ?? ''
 if (name === 'ChunkLoadError') return true
 if (msg.includes('Loading chunk') && msg.includes('failed')) return true
 if (msg.includes('Failed to fetch dynamically imported module')) return true
 if (msg.includes('dynamically imported module') && msg.includes('error')) return true
 if (/\/static\/js\/.*\.chunk\.js/i.test(msg) && msg.includes('failed')) return true
 return false
}

function readReloadAttempts(): number {
 try {
 const raw = sessionStorage.getItem(SESSION_RELOAD_ATTEMPTS_KEY)
 const n = raw ? parseInt(raw, 10) : 0
 return Number.isFinite(n) && n >= 0 ? n : 0
 } catch {
 return 0
 }
}

function incrementReloadAttempts(): number {
 const next = readReloadAttempts() + 1
 try {
 sessionStorage.setItem(SESSION_RELOAD_ATTEMPTS_KEY, String(next))
 } catch {
 /* ignore */
 }
 return next
}

function readAutoReloadAttempts(): number {
 try {
 const raw = sessionStorage.getItem(SESSION_AUTO_RELOAD_ATTEMPTS_KEY)
 const n = raw ? parseInt(raw, 10) : 0
 return Number.isFinite(n) && n >= 0 ? n : 0
 } catch {
 return 0
 }
}

function incrementAutoReloadAttempts(): number {
 const next = readAutoReloadAttempts() + 1
 try {
 sessionStorage.setItem(SESSION_AUTO_RELOAD_ATTEMPTS_KEY, String(next))
 } catch {
 /* ignore */
 }
 return next
}

export function getChunkRecoveryReloadAttempts(): number {
 return readReloadAttempts()
}

export async function unregisterAllServiceWorkers(): Promise<void> {
 if (!('serviceWorker' in navigator)) return
 try {
 const regs = await navigator.serviceWorker.getRegistrations()
 await Promise.all(regs.map((r) => r.unregister()))
 } catch {
 /* ignore */
 }
}

export async function deleteAllBrowserCaches(): Promise<void> {
 if (!('caches' in window)) return
 try {
 const names = await caches.keys()
 await Promise.all(names.map((n) => caches.delete(n)))
 } catch {
 /* ignore */
 }
}

async function askActiveServiceWorkerToClearCaches(): Promise<void> {
 if (!('serviceWorker' in navigator)) return
 try {
 navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_RUNTIME_CACHES' })
 } catch {
 /* ignore */
 }
}

function reloadBypassingHttpCache(): void {
 const url = new URL(window.location.href)
 url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()))
 window.location.replace(url.toString())
}

function removeRecoveryCacheBustParam(): void {
 if (typeof window === 'undefined') return
 const url = new URL(window.location.href)
 if (!url.searchParams.has(CACHE_BUST_PARAM)) return
 url.searchParams.delete(CACHE_BUST_PARAM)
 window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`)
}

function isElectronRuntime(): boolean {
 return window.location.protocol === 'file:' || Boolean((window as any)?.electronAPI?.isElectron)
}

function normalizeAssetPath(value: string | undefined): string | null {
 if (!value) return null
 try {
 return new URL(value, window.location.origin).pathname
 } catch {
 return value.startsWith('/') ? value : `/${value}`
 }
}

function publicUrlPath(path: string): string {
 const raw = process.env.PUBLIC_URL ?? ''
 if (!raw || raw === '/'|| raw === '.'|| raw === './') return path
 return `${raw.replace(/\/$/, '')}${path}`
}

function getCurrentDocumentDeploymentSignature(): string | null {
 const script = Array.from(document.scripts)
 .map((node) => normalizeAssetPath(node.getAttribute('src') ?? undefined))
 .find((src): src is string => Boolean(src && /\/static\/js\/main\.[^.]+\.js$/i.test(src)))

 const stylesheet = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
 .map((node) => normalizeAssetPath(node.getAttribute('href') ?? undefined))
 .find((href): href is string => Boolean(href && /\/static\/css\/main\.[^.]+\.css$/i.test(href)))

 if (!script && !stylesheet) return null
 return [script, stylesheet].filter(Boolean).join('|')
}

async function fetchLatestDeploymentSignature(): Promise<string | null> {
 const response = await fetch(publicUrlPath('/asset-manifest.json'), {
 cache: 'no-store',
 headers: {
 'Cache-Control': 'no-cache',
 Pragma: 'no-cache',
 },
 })
 if (!response.ok) return null

 const manifest = (await response.json()) as { files?: Record<string, string | undefined> }
 const mainScript = normalizeAssetPath(manifest.files?.['main.js'])
 const mainStylesheet = normalizeAssetPath(manifest.files?.['main.css'])
 if (!mainScript && !mainStylesheet) return null
 return [mainScript, mainStylesheet].filter(Boolean).join('|')
}

/** Clears SW + Cache Storage then reloads, up to SESSION_MAX_FULL_RECOVERY_RELOADS per tab session */
export async function clearCachesAndReloadForNewDeployment(): Promise<{ reloaded: boolean; attempts: number }> {
 const current = readReloadAttempts()
 if (isCheckoutInFlight()) {
  return { reloaded: false, attempts: current }
 }
 if (current >= SESSION_MAX_FULL_RECOVERY_RELOADS) {
 return { reloaded: false, attempts: current }
 }

 incrementReloadAttempts()
 await askActiveServiceWorkerToClearCaches()
 await unregisterAllServiceWorkers()
 await deleteAllBrowserCaches()

 reloadBypassingHttpCache()
 return { reloaded: true, attempts: current + 1 }
}

export async function autoRecoverFromChunkLoadError(): Promise<boolean> {
 if (typeof window === 'undefined') return false
 if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
 if (readAutoReloadAttempts() >= SESSION_MAX_AUTO_RECOVERY_RELOADS) return false

 incrementAutoReloadAttempts()
 const result = await clearCachesAndReloadForNewDeployment()
 return result.reloaded
}

export function installDeploymentUpdateWatcher(): void {
 if (typeof window === 'undefined' || typeof document === 'undefined') return
 if (process.env.NODE_ENV !== 'production') return
 if (isElectronRuntime()) return

 let checking = false
 let reloading = false

 const check = async () => {
 if (checking || reloading) return
 if (typeof navigator !== 'undefined' && navigator.onLine === false) return

 checking = true
 try {
 const latestSignature = await fetchLatestDeploymentSignature()
 if (!latestSignature) return

 const currentSignature = getCurrentDocumentDeploymentSignature()
 localStorage.setItem(DEPLOYMENT_SIGNATURE_KEY, latestSignature)

 if (!currentSignature || currentSignature === latestSignature) return

 reloading = true
 await clearCachesAndReloadForNewDeployment()
 } catch {
 /* ignore deployment checks; chunk recovery remains the fallback */
 } finally {
 checking = false
 }
 }

 window.setTimeout(check, 5000)
 window.setInterval(check, DEPLOYMENT_CHECK_INTERVAL_MS)
 window.addEventListener('focus', check)
 document.addEventListener('visibilitychange', () => {
 if (document.visibilityState === 'visible') void check()
 })
}

function showGlobalChunkRecoveryPanel(): void {
 if (typeof document === 'undefined') return
 if (document.getElementById(GLOBAL_PANEL_ID)) return

 const panel = document.createElement('div')
 panel.id = GLOBAL_PANEL_ID
 panel.dir = 'rtl'
 panel.setAttribute('role', 'alertdialog')
 panel.setAttribute('aria-modal', 'true')
 panel.setAttribute('aria-labelledby', `${GLOBAL_PANEL_ID}-title`)
 panel.style.cssText = [
 'position:fixed',
 'inset:0',
 'z-index:2147483647',
 'display:flex',
 'align-items:center',
 'justify-content:center',
 'background:rgba(15,23,42,.62)',
 'padding:24px',
 'font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
 ].join(';')

 const lang = (() => {
  try {
   const raw = localStorage.getItem('i18nextLng') || document.documentElement.getAttribute('lang') || 'ar'
   if (raw.startsWith('en')) return 'en'
   if (raw.startsWith('fr')) return 'fr'
   return 'ar'
  } catch {
   return 'ar'
  }
 })()
 const copy = {
  en: {
   title: 'A new system version is available. Refresh the page to continue.',
   body: 'Old cached files will be cleared from the browser, then the system will reload.',
   refresh: 'Refresh now',
  },
  fr: {
   title: 'Une nouvelle version du système est disponible. Actualisez la page pour continuer.',
   body: 'Les anciens fichiers en cache seront effacés, puis le système sera rechargé.',
   refresh: 'Actualiser maintenant',
  },
  ar: {
   title: 'تم إصدار نسخة جديدة من النظام. يرجى تحديث الصفحة للمتابعة.',
   body: 'سيتم مسح ملفات النسخة القديمة من ذاكرة المتصفح ثم إعادة تحميل النظام.',
   refresh: 'تحديث الآن',
  },
 }[lang as 'en' | 'fr' | 'ar']

 panel.innerHTML = `
 <div style="max-width:460px;width:100%;background:#fff;color:#0f172a;border-radius:16px;padding:28px;text-align:center;box-shadow:0 24px 70px rgba(15,23,42,.28)">
 <h1 id="${GLOBAL_PANEL_ID}-title" style="font-size:20px;line-height:1.5;margin:0 0 12px;font-weight:700">${copy.title}</h1>
 <p style="font-size:14px;line-height:1.8;margin:0 0 20px;color:#475569">${copy.body}</p>
 <button type="button"data-refresh style="border:0;border-radius:10px;background:#111827;color:#fff;padding:10px 18px;font-size:14px;cursor:pointer">${copy.refresh}</button>
 </div>
 `

 panel.querySelector<HTMLButtonElement>('[data-refresh]')?.addEventListener('click', () => {
 void clearCachesAndReloadForNewDeployment()
 })

 document.body.appendChild(panel)
}

export function installGlobalChunkLoadRecoveryListener(): void {
 if (typeof window === 'undefined') return

 removeRecoveryCacheBustParam()

 const handleChunkFailure = (error: unknown) => {
 if (!isChunkLoadError(error)) return
 window.dispatchEvent(new CustomEvent('erp:chunk-load-error', { detail: { error } }))
 void autoRecoverFromChunkLoadError().then((started) => {
 if (!started) showGlobalChunkRecoveryPanel()
 })
 }

 window.addEventListener('error', (event) => {
 handleChunkFailure(event.error ?? event.message)
 })

 window.addEventListener('unhandledrejection', (event) => {
 handleChunkFailure(event.reason)
 })
}
